import { type ButtonProps, type TagProps } from '@loadsmart/miranda-react'

import { type AppointmentSlot, type StopAppointment } from 'core/appointments/domain/Appointment'
import { ENVIRONMENT, EnvType } from 'core/infra/environment'
import { formatDate, parseISO } from 'core/utils/date'

const SCHEDULED_AT_FORMAT = '	E, MMMM dd'
const TIME_FORMAT = 'HH:MM'

export function formatScheduleDate(appointmentDate: AppointmentSlot) {
  if (appointmentDate.type === 'between') {
    const startDate = formatDate(parseISO(appointmentDate.start), SCHEDULED_AT_FORMAT).trim()
    const endDate = formatDate(parseISO(appointmentDate.end), SCHEDULED_AT_FORMAT).trim()

    return `Date range: ${startDate} - ${endDate}`
  }

  return formatDate(parseISO(appointmentDate.start), SCHEDULED_AT_FORMAT).trim()
}

export function formatScheduleTime(date: string) {
  return formatDate(parseISO(date), TIME_FORMAT)
}

export function formatDialogTitle(appointment?: StopAppointment) {
  if (!appointment || appointment.status === 'pending') {
    return 'Schedule'
  }
  return 'Re-Schedule'
}

export function formatScheduleReasonLabel(appointment: StopAppointment) {
  return appointment.status === 'confirmed' ? 'Reschedule reason' : 'Reschedule'
}

export function formatStatusTagColor(status: string): TagProps['variant'] {
  if (status === 'pending') {
    return 'danger'
  }

  if (status === 'confirmed') {
    return 'success'
  }

  return 'warning'
}

export function formatActionLabel(status: string) {
  if (status === 'confirmed') {
    return 'Open Appointment'
  }
  return 'Schedule'
}

export function formatActionButtonVariant(status: string): ButtonProps['variant'] {
  if (status === 'confirmed') {
    return 'tertiary'
  }
  return 'primary'
}

export function formatMisschedulingReason(reason: string) {
  return reason.replaceAll('-', ' ')
}

function formatScheduleBaseURL(externalReferenceUuid: string) {
  if (ENVIRONMENT === EnvType.Production) {
    return `https://schedule.opendock.com/appointments/${externalReferenceUuid}`
  }

  return `https://schedule.staging.opendock.com/appointments/${externalReferenceUuid}`
}

function formatWarehouseBaseURL(warehouseUUID: string) {
  if (ENVIRONMENT === EnvType.Production) {
    return `https://schedule.opendock.com/warehouses/${warehouseUUID}`
  }

  return `https://schedule-staging.opendock.com/warehouses/${warehouseUUID}`
}

export function formatAppointmentURL(appointment?: StopAppointment, warehouseUUID?: string | null) {
  if (appointment?.externalReferenceUuid) {
    return formatScheduleBaseURL(appointment.externalReferenceUuid)
  }

  if (warehouseUUID) {
    return formatWarehouseBaseURL(warehouseUUID)
  }
}
